import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useNavigate } from "react-router-dom";

import {
  DashboardParams,
  ProposalBoardCard,
  ProposalsPipelineView,
  useGetProposalsPipelineQuery,
} from "../../store/apis/commercialDashboardApi";
import KpiCard from "./KpiCard";
import { Funnel, hatch, Legend, StackBar } from "./DashboardCharts";
import { SheetTarget } from "./DashboardSheets";
import {
  BarRow,
  BlockGrid,
  Empty,
  KpiGrid,
  QualityChip,
  Section,
} from "./DashboardBits";
import {
  amountText,
  BUSINESS_LINE_LABEL,
  days,
  ENGAGEMENT_LABEL,
  euro,
  FUNNEL_STEP_LABEL,
  HOUSE_NAME,
  label,
  LOST_REASON_LABEL,
  ORIGIN_LABEL,
  percent,
  PROPOSAL_STATUS_LABEL,
  shortDate,
} from "./dashboardFormat";

type SubTab = "board" | "analysis";

const BOARD_STATUSES = [
  "BACKLOG",
  "EN_PROCESO",
  "ENTREGADA",
  "ACEPTADA",
  "RECHAZADA",
] as const;

const HOUSE_SHORT: Record<string, string> = {
  HOUSE_OF_MEDIA: "HoM",
  HOUSE_OF_DIGITAL: "HoD",
  HOUSE_OF_STORIES: "HoS",
  HOUSE_OF_AI: "HoAI",
};

/** La línea de estado de cada tarjeta, según su columna. */
const statusLine = (
  card: ProposalBoardCard
): { text: string; late: boolean } | null => {
  if (card.daysToDeadline !== null && card.daysToDeadline !== undefined) {
    return card.daysToDeadline < 0
      ? { text: `Vencida hace ${-card.daysToDeadline} d`, late: true }
      : {
          text: `Faltan ${card.daysToDeadline} d para la fecha comprometida`,
          late: false,
        };
  }
  if (card.daysWaiting !== null && card.daysWaiting !== undefined) {
    return {
      text: `${card.daysWaiting} d esperando respuesta${
        card.probability !== null ? ` · ${card.probability} %` : ""
      }`,
      late: false,
    };
  }
  if (card.status === "ENTREGADA") {
    // Las entregadas antes del 8/10/2026 no guardaron la fecha de entrega.
    return { text: "Entregada · sin fecha de entrega", late: false };
  }
  if (card.status === "RECHAZADA") {
    return {
      text: `Motivo: ${label(LOST_REASON_LABEL, card.lostReason ?? "SIN_MOTIVO")}`,
      late: false,
    };
  }
  if (card.status === "ACEPTADA" && card.decidedAt) {
    return { text: `Aceptada el ${shortDate(card.decidedAt)}`, late: false };
  }
  return null;
};

const ProposalCard: React.FC<{
  card: ProposalBoardCard;
  onOpen: (target: SheetTarget) => void;
}> = ({ card, onOpen }) => {
  const theme = useTheme();
  const line = statusLine(card);
  const open = () => onOpen({ type: "proposal", id: card.id, card });
  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
      data-testid={`proposal-card-${card.id}`}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderLeft: line?.late
          ? `4px solid ${theme.palette.error.main}`
          : undefined,
        borderRadius: 2,
        p: 1.25,
        cursor: "pointer",
        backgroundColor: theme.palette.background.default,
        "&:hover": { borderColor: theme.palette.primary.main },
      }}
    >
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {card.clientName}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
        >
          {amountText(card.amount)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {card.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontFamily: "monospace" }}
        display="block"
      >
        {card.proposalNumber}
      </Typography>
      <Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={card.businessLine}
          sx={{ height: 18, fontSize: 10 }}
        />
        {card.engagement && (
          <Chip
            size="small"
            variant="outlined"
            label={label(ENGAGEMENT_LABEL, card.engagement)}
            sx={{ height: 18, fontSize: 10 }}
          />
        )}
        {card.houses.map((house) => (
          <Chip
            key={house}
            size="small"
            variant="outlined"
            label={HOUSE_SHORT[house] ?? house}
            sx={{ height: 18, fontSize: 10 }}
          />
        ))}
      </Stack>
      {line && (
        <Typography
          variant="caption"
          display="block"
          mt={0.5}
          color={line.late ? "error.main" : "text.secondary"}
        >
          {line.text}
        </Typography>
      )}
      {card.ownerName && (
        <Typography variant="caption" color="text.secondary" display="block">
          {card.ownerName}
        </Typography>
      )}
    </Paper>
  );
};

const ProposalBoardView: React.FC<{
  cards: ProposalBoardCard[];
  onOpen: (target: SheetTarget) => void;
}> = ({ cards, onOpen }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        overflowX: "auto",
        pb: 1.5,
        alignItems: "flex-start",
      }}
    >
      {BOARD_STATUSES.map((status) => {
        const column = cards.filter((card) => card.status === status);
        const total = column.reduce((sum, card) => sum + (card.amount ?? 0), 0);
        return (
          <Paper
            key={status}
            elevation={0}
            data-testid={`proposal-column-${status}`}
            sx={{
              minWidth: 248,
              width: 248,
              flexShrink: 0,
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[200]}`,
              p: 1.5,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography variant="subtitle2">
                {PROPOSAL_STATUS_LABEL[status]}
              </Typography>
              <Chip
                size="small"
                variant={
                  status === "ACEPTADA" || status === "RECHAZADA"
                    ? "filled"
                    : "outlined"
                }
                color={
                  status === "ACEPTADA"
                    ? "success"
                    : status === "RECHAZADA"
                      ? "error"
                      : "default"
                }
                label={column.length}
              />
            </Stack>
            {column.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                {euro.format(total)}
              </Typography>
            )}
            <Stack gap={1}>
              {column.map((card) => (
                <ProposalCard key={card.id} card={card} onOpen={onOpen} />
              ))}
            </Stack>
          </Paper>
        );
      })}
    </Box>
  );
};

const ProposalTableView: React.FC<{
  cards: ProposalBoardCard[];
  onOpen: (target: SheetTarget) => void;
}> = ({ cards, onOpen }) => {
  const theme = useTheme();
  return (
    <TableContainer
      sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Número</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Negocio</TableCell>
            <TableCell>Houses</TableCell>
            <TableCell align="right">Importe</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Comprometida</TableCell>
            <TableCell>Situación</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cards.map((card) => {
            const line = statusLine(card);
            return (
              <TableRow
                key={card.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => onOpen({ type: "proposal", id: card.id, card })}
              >
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {card.proposalNumber}
                </TableCell>
                <TableCell>{card.clientName}</TableCell>
                <TableCell>
                  {label(PROPOSAL_STATUS_LABEL, card.status)}
                </TableCell>
                <TableCell>{card.businessLine}</TableCell>
                <TableCell>
                  {card.houses
                    .map((house) => HOUSE_SHORT[house] ?? house)
                    .join(" · ") || "—"}
                </TableCell>
                <TableCell align="right">{amountText(card.amount)}</TableCell>
                <TableCell>{card.ownerName ?? "—"}</TableCell>
                <TableCell>{shortDate(card.deliveryDeadline)}</TableCell>
                <TableCell
                  sx={{ color: line?.late ? "error.main" : undefined }}
                >
                  {line?.text ?? "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface TabProps {
  view: ProposalsPipelineView;
  onOpen: (target: SheetTarget) => void;
}

const BoardTab: React.FC<TabProps> = ({ view, onOpen }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"board" | "table">("board");
  const { open, preparation, delivered, overdueInternal } = view.kpis;
  return (
    <Stack gap={2}>
      <KpiGrid columns={4}>
        <KpiCard
          label="Abiertas"
          value={String(open.total)}
          caption={`${open.backlog} en backlog · ${open.inProgress} en proceso · ${open.delivered} entregadas`}
        />
        <KpiCard
          label="En preparación"
          value={euro.format(preparation.amount)}
          caption={
            preparation.withoutBudget > 0
              ? `${preparation.count} propuestas, ${preparation.withoutBudget} sin presupuesto`
              : `${preparation.count} propuestas`
          }
          orientative
        />
        <KpiCard
          label="Entregadas, esperando respuesta"
          value={euro.format(delivered.amount)}
          caption={`${delivered.count} propuestas · ${days(delivered.avgDaysWaiting)} de espera media · ponderado ${
            delivered.weightedAmount === null
              ? "—"
              : euro.format(delivered.weightedAmount)
          }`}
          orientative
        />
        <KpiCard
          label="Entregas internas vencidas"
          value={String(overdueInternal)}
          caption="Sin entregar y con la fecha comprometida pasada"
          tone={overdueInternal > 0 ? "error" : undefined}
        />
      </KpiGrid>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="caption" color="text.secondary">
          Aceptadas y rechazadas: solo las respondidas en el periodo. Para mover
          una propuesta de columna, usa el tablero de propuestas.
        </Typography>
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate("/commercial-proposals")}
          >
            Ir al tablero de propuestas
          </Button>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, next) => next && setMode(next)}
          >
            <ToggleButton value="board" aria-label="Vista de tablero">
              <ViewColumnIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="table" aria-label="Vista de tabla">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {mode === "board" ? (
        <ProposalBoardView cards={view.board} onOpen={onOpen} />
      ) : (
        <ProposalTableView cards={view.board} onOpen={onOpen} />
      )}
    </Stack>
  );
};

/** Un tramo que se despliega con sus propuestas. */
const ExpandableBucket: React.FC<{
  title: string;
  share: number;
  value: string;
  cards: ProposalBoardCard[];
  color?: string;
  onOpen: (target: SheetTarget) => void;
}> = ({ title, share, value, cards, color, onOpen }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <BarRow
        label={title}
        share={share}
        value={value}
        color={color}
        onClick={
          cards.length > 0 ? () => setOpen((current) => !current) : undefined
        }
      />
      <Collapse in={open} unmountOnExit>
        <Stack pl={2} py={0.5}>
          {cards.map((card) => (
            <Stack
              key={card.id}
              direction="row"
              justifyContent="space-between"
              gap={1}
              py={0.25}
              sx={{ cursor: "pointer" }}
              onClick={() => onOpen({ type: "proposal", id: card.id, card })}
            >
              <Typography variant="body2">
                {card.clientName}{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontFamily: "monospace" }}
                >
                  {card.proposalNumber}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.deliveredAt
                  ? `Entregada el ${shortDate(card.deliveredAt)}`
                  : ""}
                {card.daysWaiting !== null ? ` · ${card.daysWaiting} d` : ""}
                {card.probability !== null
                  ? ` · ${card.probability} %`
                  : ""} · {amountText(card.amount)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

const AnalysisTab: React.FC<TabProps> = ({ view, onOpen }) => {
  const theme = useTheme();
  const { analysis } = view;
  const firstFunnel = analysis.funnel[0]?.count ?? 0;
  const maxAging = Math.max(1, ...analysis.aging.map((row) => row.amount));
  const maxLoss = Math.max(1, ...analysis.lossReasons.map((row) => row.amount));
  const maxHouse = Math.max(
    1,
    ...analysis.byHouse.map((row) => row.preparationAmount + row.openAmount)
  );

  return (
    <Stack gap={2}>
      <KpiGrid columns={4}>
        <KpiCard
          label="Negocio abierto"
          value={euro.format(view.kpis.delivered.amount)}
          caption={`Ponderado ${
            view.kpis.delivered.weightedAmount === null
              ? "— (sin probabilidades en Twenty)"
              : euro.format(view.kpis.delivered.weightedAmount)
          }`}
          orientative
        />
        <KpiCard
          label="Negocio ganado"
          value={euro.format(analysis.closeRate.acceptedAmount)}
          caption={`${analysis.closeRate.accepted} aceptadas en el periodo`}
          orientative
        />
        <KpiCard
          label="Tasa de cierre"
          value={percent(analysis.closeRate.byCount)}
          caption={`Por valor ${percent(analysis.closeRate.byValue)} · ${analysis.closeRate.accepted} de ${
            analysis.closeRate.accepted + analysis.closeRate.rejected
          } con respuesta`}
        />
        <KpiCard
          label="Entrada → aceptación"
          value={days(analysis.cycle.entryToAcceptance.mean)}
          caption={`Mediana ${days(analysis.cycle.entryToAcceptance.median)} · entrega ${days(
            analysis.cycle.entryToDelivery.mean
          )} + respuesta ${days(analysis.cycle.deliveryToResponse.mean)}`}
        />
      </KpiGrid>

      <BlockGrid>
        <Section
          title="Conversión"
          subtitle="Sobre las propuestas con entrada en el periodo"
        >
          {firstFunnel === 0 ? (
            <Empty>No entró ninguna propuesta en el periodo.</Empty>
          ) : (
            <Funnel
              rows={analysis.funnel.map((step) => ({
                label: FUNNEL_STEP_LABEL[step.step],
                count: step.count,
                value: percent(step.fromStart),
                valueSub: "de las solicitadas",
              }))}
            />
          )}
        </Section>

        <Section title="Eficiencia en la preparación">
          <Stack gap={0.75}>
            <Typography variant="body2">
              Entrada → entrega:{" "}
              <b>{days(analysis.cycle.entryToDelivery.mean)}</b> de media,{" "}
              {days(analysis.cycle.entryToDelivery.median)} de mediana (
              {analysis.cycle.entryToDelivery.count} entregadas)
            </Typography>
            <Typography variant="body2">
              Entregadas en fecha: <b>{percent(analysis.cycle.onTimeRate)}</b>
            </Typography>
            <Typography variant="body2">
              Entrega → respuesta del cliente:{" "}
              <b>{days(analysis.cycle.deliveryToResponse.mean)}</b> de media
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Las horas por propuesta llegarán con Toggl.
            </Typography>
          </Stack>
        </Section>

        <Section
          title="Antigüedad del negocio abierto"
          subtitle="Entregado por días desde la entrega. Pulsa un tramo para ver sus propuestas"
          action={<QualityChip />}
        >
          {analysis.aging.map((row) => (
            <ExpandableBucket
              key={row.bucket}
              title={`${row.bucket} días`}
              share={row.amount / maxAging}
              value={`${row.count} · ${euro.format(row.amount)}`}
              cards={row.proposals}
              color={
                row.bucket === "30+"
                  ? theme.palette.error.main
                  : row.bucket === "15-30"
                    ? theme.palette.warning.main
                    : undefined
              }
              onOpen={onOpen}
            />
          ))}
        </Section>

        <Section
          title="Negocio abierto por House"
          subtitle="Entregado + en preparación, repartido por el peso de cada House"
          action={<QualityChip />}
        >
          {analysis.byHouse.length === 0 ? (
            <Empty>Sin datos.</Empty>
          ) : (
            <Stack gap={1.5}>
              {analysis.byHouse.map((row) => (
                <Box key={row.house}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2">
                      {label(HOUSE_NAME, row.house)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {euro.format(row.preparationAmount + row.openAmount)}
                    </Typography>
                  </Stack>
                  <StackBar
                    total={maxHouse}
                    height={12}
                    legend={false}
                    segments={[
                      {
                        label: "Entregado",
                        value: row.openAmount,
                        background: hatch(theme.palette.primary.main),
                      },
                      {
                        label: "En preparación",
                        value: row.preparationAmount,
                        background: hatch(theme.palette.grey[500]),
                      },
                    ]}
                  />
                </Box>
              ))}
              <Legend
                items={[
                  {
                    label: "Entregado (valorado cuando exista la valoración)",
                    background: hatch(theme.palette.primary.main),
                    dashed: true,
                  },
                  {
                    label: "En preparación (orientativo)",
                    background: hatch(theme.palette.grey[500]),
                    dashed: true,
                  },
                ]}
              />
            </Stack>
          )}
        </Section>

        <Section title="Resultados por House" action={<QualityChip />}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>House</TableCell>
                  <TableCell align="right">En preparación</TableCell>
                  <TableCell align="right">Abierto</TableCell>
                  <TableCell align="right">Ganado</TableCell>
                  <TableCell align="right">Cierre</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analysis.byHouse.map((row) => (
                  <TableRow key={row.house}>
                    <TableCell>{label(HOUSE_NAME, row.house)}</TableCell>
                    <TableCell align="right">
                      {euro.format(row.preparationAmount)}
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(row.openAmount)}
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(row.wonAmount)}
                    </TableCell>
                    <TableCell align="right">
                      {percent(row.closeRate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section title="Rendimiento por origen" action={<QualityChip />}>
          {analysis.byOrigin.length === 0 ? (
            <Empty>Sin datos.</Empty>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Origen</TableCell>
                    <TableCell align="right">Propuestas</TableCell>
                    <TableCell align="right">Entregadas</TableCell>
                    <TableCell align="right">Ganadas</TableCell>
                    <TableCell align="right">Cierre</TableCell>
                    <TableCell align="right">Abierto</TableCell>
                    <TableCell align="right">Ganado</TableCell>
                    <TableCell align="right">Preparación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analysis.byOrigin.map((row) => (
                    <TableRow key={row.origin}>
                      <TableCell>{label(ORIGIN_LABEL, row.origin)}</TableCell>
                      <TableCell align="right">{row.proposals}</TableCell>
                      <TableCell align="right">{row.delivered}</TableCell>
                      <TableCell align="right">{row.accepted}</TableCell>
                      <TableCell align="right">
                        {percent(row.closeRate)}
                      </TableCell>
                      <TableCell align="right">
                        {euro.format(row.openAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {euro.format(row.wonAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {days(row.avgPreparationDays)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Section>

        <Section title="Tasa de cierre por negocio">
          {analysis.byBusinessLine.map((row) => (
            <BarRow
              key={row.businessLine}
              label={BUSINESS_LINE_LABEL[row.businessLine]}
              share={row.closeRate ?? 0}
              value={percent(row.closeRate)}
              caption={`${row.accepted} aceptadas · ${row.rejected} rechazadas`}
            />
          ))}
        </Section>

        <Section
          title="Motivos de pérdida"
          subtitle="Por valor. Pulsa un motivo para ver sus propuestas"
          action={<QualityChip />}
        >
          {analysis.lossReasons.length === 0 ? (
            <Empty>No hay propuestas rechazadas en el periodo.</Empty>
          ) : (
            analysis.lossReasons.map((row) => (
              <ExpandableBucket
                key={row.reason}
                title={label(LOST_REASON_LABEL, row.reason)}
                share={row.amount / maxLoss}
                value={`${row.count} · ${euro.format(row.amount)}`}
                cards={row.proposals}
                color={theme.palette.error.main}
                onOpen={onOpen}
              />
            ))
          )}
        </Section>
      </BlockGrid>

      <Typography variant="caption" color="text.secondary">
        Pendiente de la valoración de la Proposal Factory: rentabilidad del
        negocio abierto, orientativo frente a valoración final y fiabilidad de
        la probabilidad. Pendiente de Toggl: horas de preparación, coste y CAC.
      </Typography>
    </Stack>
  );
};

interface Props {
  params: DashboardParams;
  onOpen: (target: SheetTarget) => void;
}

/** 03 · Pipeline de propuestas (NB + EB). */
const ProposalsPipelineTab: React.FC<Props> = ({ params, onOpen }) => {
  const theme = useTheme();
  const [tab, setTab] = useState<SubTab>("board");
  const { data, isLoading, isError } = useGetProposalsPipelineQuery(params);
  const view = data?.data;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !view) {
    return (
      <Alert severity="error">
        No se pudo cargar el pipeline de propuestas.
      </Alert>
    );
  }

  return (
    <Stack gap={2}>
      <Tabs
        value={tab}
        onChange={(_, next: SubTab) => setTab(next)}
        sx={{
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          minHeight: 36,
        }}
      >
        <Tab value="board" label="Tablero" sx={{ minHeight: 36 }} />
        <Tab
          value="analysis"
          label="Análisis comercial"
          sx={{ minHeight: 36 }}
        />
      </Tabs>
      {tab === "board" ? (
        <BoardTab view={view} onOpen={onOpen} />
      ) : (
        <AnalysisTab view={view} onOpen={onOpen} />
      )}
    </Stack>
  );
};

export default ProposalsPipelineTab;
